import { useMemo } from "react";
import { generatePath, Navigate, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { CALENDAR_DAY_ROUTE, CALENDAR_LESSON_ROUTE, CALENDAR_ROUTE } from "../../../app/router/routes";
import { ICalendarFilter, IGetCalendarFilterOptionsResponse } from "../../../entities/calendar";
import { CalendarDay } from "../../../widgets/calendarDay";
import classes from './calendarDay.module.scss'
import { ChangeDayInCalendar } from "../../../features/changeDayInCalendar";
import { scrollLayoutToTop } from "../../../shared/lib/helpers/scrollLayoutToTop";

interface ICalendarOutletContext {
    filter: ICalendarFilter;
    filterOptions: IGetCalendarFilterOptionsResponse;
    setIsDayContentOpen: (isOpen: boolean) => void;
}

const getDateFromParam = (dateParam?: string): Date | null => {
    const match = dateParam?.match(/^(\d{4})-(\d{2})-(\d{2})$/)

    if(!match) return null

    const year = Number(match[1])
    const month = Number(match[2])
    const day = Number(match[3])
    const date = new Date(year, month - 1, day)

    if(
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ){
        return null
    }

    return date
}

const getDatePath = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `/calendar/day/${year}-${month}-${day}`
}

export default function CalendarDayPage() {

    const {date: dateParam, lessonId} = useParams<{date: string; lessonId?: string}>()
    const {filter, filterOptions, setIsDayContentOpen} = useOutletContext<ICalendarOutletContext>()
    const location = useLocation()
    const navigate = useNavigate()
    const date = useMemo(() => getDateFromParam(dateParam), [dateParam])

    if(!date){
        return (
            <Navigate
                to={{pathname: CALENDAR_ROUTE.path, search: location.search}}
                replace
            />
        )
    }

    return (
        <section className={classes.container}>
            <CalendarDay
                date={date}
                filter={filter}
                filterOptions={filterOptions}
                lessonId={lessonId}
                onContentOpen={scrollLayoutToTop}
                onContentChange={setIsDayContentOpen}
                onSelectLesson={selectedLessonId => {
                    navigate({
                        pathname: generatePath(CALENDAR_LESSON_ROUTE.path, {
                            date: dateParam,
                            lessonId: selectedLessonId,
                        }),
                        search: location.search,
                    })
                }}
                onCloseLesson={() => {
                    navigate({
                        pathname: generatePath(CALENDAR_DAY_ROUTE.path, {date: dateParam}),
                        search: location.search,
                    }, {replace: true})
                }}
            />
            <section className={classes.calendarSticky}>
                <ChangeDayInCalendar
                    date={date}
                    filter={filter}
                    onSelectDate={nextDate => {
                        navigate({pathname: getDatePath(nextDate), search: location.search})
                    }}
                />
            </section>
        </section>
    )
}
